/**
 * Component Metadata Service
 *
 * Handles component metadata loading, comparison, and synchronization
 * for composite assets to maintain data integrity and provide user awareness
 * of metadata changes.
 */

import { Asset } from '../types/asset.types';
import assetService from '../api/assetService';
import { logger } from '../utils/logger';
import { enhancedTaxonomyService } from './enhancedTaxonomyService';

/**
 * Detects meaningful changes between old and new values, ignoring false positives
 * This prevents the metadata sync dialog from appearing unnecessarily
 */
function detectMeaningfulChange(
  oldValue: any,
  newValue: any,
  fieldName: string
): boolean {
  // Handle null/undefined equivalence
  if ((oldValue == null) !== (newValue == null)) {
    return true; // One is null/undefined, the other isn't
  }

  if (oldValue == null && newValue == null) {
    return false; // Both are null/undefined
  }

  // Handle numeric fields with precision issues
  if (typeof oldValue === 'number' && typeof newValue === 'number') {
    return Math.abs(oldValue - newValue) > 0.001; // Allow for floating point precision
  }

  // Handle arrays
  if (Array.isArray(oldValue) && Array.isArray(newValue)) {
    if (oldValue.length !== newValue.length) return true;

    // Sort arrays before comparison to ignore order differences
    const sortedOld = [...oldValue].sort();
    const sortedNew = [...newValue].sort();

    return JSON.stringify(sortedOld) !== JSON.stringify(sortedNew);
  }

  // Handle objects
  if (typeof oldValue === 'object' && typeof newValue === 'object') {
    return JSON.stringify(oldValue) !== JSON.stringify(newValue);
  }

  // Handle strings with whitespace normalization
  if (typeof oldValue === 'string' && typeof newValue === 'string') {
    return oldValue.trim() !== newValue.trim();
  }

  // Default comparison for other types
  return oldValue !== newValue;
}

export interface ComponentMetadataChange {
  field: string;
  oldValue: any;
  newValue: any;
  lastUpdated?: string;
}

export interface ComponentMetadataComparison {
  componentId: string;
  componentName: string;
  hasChanges: boolean;
  changes: ComponentMetadataChange[];
  currentMetadata: any;
  lastSyncTime?: string;
}

export interface CompositeMetadataAnalysis {
  hasAnyChanges: boolean;
  componentComparisons: ComponentMetadataComparison[];
  totalChanges: number;
  affectedComponents: number;
}

/**
 * Extract component HFNs from composite asset name
 * Format: C.PAR.4LY.003:1.016.002.002+2.012.002.001+3.001.002.001+4.009.003.001
 */
export function extractComponentHFNs(compositeAssetName: string): string[] {
  try {
    if (!compositeAssetName || !compositeAssetName.includes(':')) {
      return [];
    }

    const parts = compositeAssetName.split(':');
    if (parts.length < 2) {
      return [];
    }

    // Components are after the colon, separated by +
    const componentsString = parts[1];
    const componentMFAs = componentsString.split('+');

    // For now, return MFAs as identifiers since we'll need to convert them
    // In practice, we'd need to convert MFAs to HFNs or use MFAs for lookup
    return componentMFAs.map(mfa => mfa.trim()).filter(mfa => mfa.length > 0);
  } catch (error) {
    logger.error('Error extracting component HFNs:', error);
    return [];
  }
}

/**
 * Convert MFA to HFN for component lookup
 */
async function convertMFAtoHFN(mfa: string): Promise<string | null> {
  try {
    const hfn = await enhancedTaxonomyService.convertMFAtoHFN(mfa);
    return hfn || null;
  } catch (error) {
    console.error(
      `❌ [COMPONENT SERVICE] Error converting MFA ${mfa} to HFN:`,
      error
    );
    return null;
  }
}

/**
 * Load current metadata for component assets
 */
export async function loadComponentMetadata(
  componentIds: string[]
): Promise<Map<string, Asset>> {
  const componentMap = new Map<string, Asset>();

  try {
    // First, convert all MFAs to HFNs
    const conversionPromises = componentIds.map(async componentMFA => {
      const hfn = await convertMFAtoHFN(componentMFA);
      return { mfa: componentMFA, hfn };
    });

    const conversions = await Promise.all(conversionPromises);
    console.log(`🔄 [COMPONENT SERVICE] MFA to HFN conversions:`, conversions);

    // Load components in parallel using HFNs
    const loadPromises = conversions.map(async ({ mfa, hfn }) => {
      try {
        if (!hfn) {
          console.warn(
            `⚠️ [COMPONENT SERVICE] Could not convert MFA ${mfa} to HFN`
          );
          return;
        }

        console.log(
          `🔍 [COMPONENT SERVICE] Loading metadata for component: ${hfn} (MFA: ${mfa})`
        );

        // ENHANCED SEARCH: Try multiple search approaches for better component loading
        let component = null;

        // Method 1: Search by exact HFN
        console.log(
          `🔍 [COMPONENT SERVICE] Method 1: Searching by exact HFN: ${hfn}`
        );
        const hfnSearchResult = await assetService.getAssets({
          search: hfn,
          limit: 10,
        });

        if (hfnSearchResult.data && hfnSearchResult.data.length > 0) {
          component = hfnSearchResult.data.find(
            asset => asset.name === hfn && asset.layer !== 'C'
          );

          if (component) {
            console.log(
              `✅ [COMPONENT SERVICE] Method 1 success: Found exact match for ${hfn}`
            );
          }
        }

        // Method 2: If exact search failed, try searching by MFA
        if (!component) {
          console.log(
            `🔍 [COMPONENT SERVICE] Method 2: Searching by MFA: ${mfa}`
          );
          const mfaSearchResult = await assetService.getAssets({
            search: mfa,
            limit: 10,
          });

          if (mfaSearchResult.data && mfaSearchResult.data.length > 0) {
            component = mfaSearchResult.data.find(
              asset => asset.nnaAddress === mfa && asset.layer !== 'C'
            );

            if (component) {
              console.log(
                `✅ [COMPONENT SERVICE] Method 2 success: Found component by MFA ${mfa}`
              );
            }
          }
        }

        // Method 3: If still not found, try layer-specific search
        if (!component && hfn.includes('.')) {
          const layer = hfn.split('.')[0];
          console.log(
            `🔍 [COMPONENT SERVICE] Method 3: Layer-specific search for layer ${layer}`
          );

          const layerSearchResult = await assetService.getAssets({
            layer: layer,
            limit: 50, // Search more broadly in the layer
          });

          if (layerSearchResult.data && layerSearchResult.data.length > 0) {
            component = layerSearchResult.data.find(
              asset => asset.name === hfn || asset.nnaAddress === mfa
            );

            if (component) {
              console.log(
                `✅ [COMPONENT SERVICE] Method 3 success: Found component in layer ${layer}`
              );
            }
          }
        }

        if (component) {
          componentMap.set(mfa, component);
          console.log(
            `✅ [COMPONENT SERVICE] Successfully loaded component ${hfn}:`,
            {
              name: component.name,
              layer: component.layer,
              songMetadata: !!component.songMetadata,
              updatedAt: component.updatedAt,
              nnaAddress: component.nnaAddress,
            }
          );
        } else {
          console.warn(
            `⚠️ [COMPONENT SERVICE] Component not found after all search methods: ${hfn} (MFA: ${mfa})`
          );
        }
      } catch (error) {
        console.error(
          `❌ [COMPONENT SERVICE] Error loading component ${hfn}:`,
          error
        );
      }
    });

    await Promise.all(loadPromises);

    console.log(
      `📊 [COMPONENT SERVICE] Loaded ${componentMap.size}/${componentIds.length} components successfully`
    );
  } catch (error) {
    logger.error('Error loading component metadata:', error);
  }

  return componentMap;
}

/**
 * Compare stored composite metadata with current component metadata
 */
export function compareComponentMetadata(
  storedMetadata: any,
  currentComponent: Asset,
  componentId: string
): ComponentMetadataComparison {
  const comparison: ComponentMetadataComparison = {
    componentId,
    componentName: currentComponent.name,
    hasChanges: false,
    changes: [],
    currentMetadata: currentComponent.songMetadata || {},
  };

  try {
    // CRITICAL FIX: Only compare song metadata for G layer (Songs) components
    // Other layers (S, L, M, W) don't have song metadata, so skip comparison
    if (currentComponent.layer !== 'G') {
      console.log(
        `📋 [COMPONENT SERVICE] Skipping metadata comparison for non-Song component ${componentId} (layer: ${currentComponent.layer})`
      );
      return comparison;
    }

    console.log(
      `🎵 [COMPONENT SERVICE] Comparing song metadata for G layer component ${componentId}`
    );

    // Focus on Song metadata for now since that's the main concern
    const storedSongMetadata = storedMetadata || {};
    const currentSongMetadata = currentComponent.songMetadata || {};

    // Fields to compare
    const fieldsToCompare = [
      'songName',
      'artistName',
      'albumName',
      'bpm',
      'genre',
      'releaseYear',
      'duration',
      'albumArtUrl',
      'originalArtistGender',
      'genderSuitability',
      'vocalRange',
      'ageAppropriateness',
    ] as const;

    for (const field of fieldsToCompare) {
      const storedValue =
        storedSongMetadata[field as keyof typeof storedSongMetadata];
      const currentValue =
        currentSongMetadata[field as keyof typeof currentSongMetadata];

      // ENHANCED COMPARISON: Only detect meaningful changes, ignore false positives
      const hasChanged = detectMeaningfulChange(
        storedValue,
        currentValue,
        field
      );

      if (hasChanged) {
        comparison.changes.push({
          field,
          oldValue: storedValue,
          newValue: currentValue,
          lastUpdated: currentComponent.updatedAt,
        });
      }
    }

    comparison.hasChanges = comparison.changes.length > 0;

    if (comparison.hasChanges) {
      console.log(
        `🔄 [COMPONENT SERVICE] Changes detected for ${componentId}:`,
        comparison.changes
      );
    }
  } catch (error) {
    logger.error(
      `Error comparing metadata for component ${componentId}:`,
      error
    );
  }

  return comparison;
}

/**
 * Analyze all component metadata changes for a composite asset
 */
export async function analyzeCompositeMetadata(
  compositeAsset: Asset
): Promise<CompositeMetadataAnalysis> {
  const analysis: CompositeMetadataAnalysis = {
    hasAnyChanges: false,
    componentComparisons: [],
    totalChanges: 0,
    affectedComponents: 0,
  };

  try {
    console.log(
      `🔍 [COMPONENT SERVICE] Starting metadata analysis for composite: ${compositeAsset.name}`
    );

    // Extract component identifiers
    const componentIds = extractComponentHFNs(compositeAsset.name);

    if (componentIds.length === 0) {
      console.log(
        `⚠️ [COMPONENT SERVICE] No components found in composite asset name: ${compositeAsset.name}`
      );
      return analysis;
    }

    console.log(
      `📋 [COMPONENT SERVICE] Found ${componentIds.length} components to analyze:`,
      componentIds
    );

    // Load current component metadata
    const currentComponents = await loadComponentMetadata(componentIds);

    // Compare each component
    for (const componentId of componentIds) {
      const currentComponent = currentComponents.get(componentId);

      if (!currentComponent) {
        console.warn(
          `⚠️ [COMPONENT SERVICE] Skipping comparison for missing component: ${componentId}`
        );
        continue;
      }

      // CRITICAL FIX: Only pass stored metadata for G layer components
      // Other layers don't have song metadata to compare
      const storedComponentMetadata =
        currentComponent.layer === 'G' ? compositeAsset.songMetadata : null;

      const comparison = compareComponentMetadata(
        storedComponentMetadata,
        currentComponent,
        componentId
      );

      analysis.componentComparisons.push(comparison);

      if (comparison.hasChanges) {
        analysis.totalChanges += comparison.changes.length;
        analysis.affectedComponents++;
      }
    }

    analysis.hasAnyChanges = analysis.totalChanges > 0;

    console.log(`📊 [COMPONENT SERVICE] Analysis complete:`, {
      hasChanges: analysis.hasAnyChanges,
      totalChanges: analysis.totalChanges,
      affectedComponents: analysis.affectedComponents,
      totalComponents: componentIds.length,
    });
  } catch (error) {
    logger.error('Error analyzing composite metadata:', error);
  }

  return analysis;
}

/**
 * Get the primary Song component from a composite asset
 * Returns the first G layer component found
 */
export function getPrimarySongComponent(
  componentComparisons: ComponentMetadataComparison[]
): ComponentMetadataComparison | null {
  // Find the first component that looks like a Song (starts with 1. which is G layer MFA)
  const songComponent = componentComparisons.find(
    comp => comp.componentId.startsWith('1.') && comp.currentMetadata
  );

  if (songComponent) {
    console.log(`🎵 [COMPONENT SERVICE] Found primary Song component:`, {
      id: songComponent.componentId,
      name: songComponent.componentName,
      hasChanges: songComponent.hasChanges,
      changeCount: songComponent.changes.length,
    });
  }

  return songComponent || null;
}

/**
 * Format metadata change for display
 */
export function formatMetadataChange(change: ComponentMetadataChange): string {
  const { field, oldValue, newValue } = change;

  // Handle special cases
  if (field === 'ageAppropriateness') {
    const oldArray = Array.isArray(oldValue) ? oldValue : [];
    const newArray = Array.isArray(newValue) ? newValue : [];
    return `${field}: [${oldArray.join(', ')}] → [${newArray.join(', ')}]`;
  }

  if (field === 'bpm' || field === 'releaseYear' || field === 'duration') {
    return `${field}: ${oldValue || 'unset'} → ${newValue || 'unset'}`;
  }

  return `${field}: "${oldValue || 'unset'}" → "${newValue || 'unset'}"`;
}

/**
 * Create update payload for syncing component metadata
 */
export function createMetadataSyncPayload(
  primarySongComponent: ComponentMetadataComparison
): any {
  if (!primarySongComponent || !primarySongComponent.currentMetadata) {
    return {};
  }

  return {
    songMetadata: primarySongComponent.currentMetadata,
  };
}

export default {
  extractComponentHFNs,
  loadComponentMetadata,
  compareComponentMetadata,
  analyzeCompositeMetadata,
  getPrimarySongComponent,
  formatMetadataChange,
  createMetadataSyncPayload,
};
